import api from "../api/api";

// Every call here hits a real, existing /auth/* route — nothing invented.
// register() always creates role="customer" server-side (confirmed in
// routers/auth_router.py); there is no separate "customer" vs "staff"
// register endpoint to choose between.

export async function register({ name, email, password, phone, photo }) {
  // `photo` is the same base64 data-URL shape as the existing self-service
  // PUT /auth/me/photo — optional, omitted entirely when not provided
  // rather than sending an explicit null for a payload that never had it.
  const payload = { name, email, password, phone };
  if (photo) payload.photo = photo;
  const res = await api.post("/auth/register", payload);
  return res.data; // UserOut
}

export async function login({ identifier, password }) {
  // Backend accepts either `email` or `username` in LoginIn and tries an
  // email match first, then a case-insensitive full-name match — so one
  // field covers both "log in with email" and "log in with full name".
  const res = await api.post("/auth/login", { email: identifier, password });
  return res.data; // { access_token, user }
}

export async function logout() {
  // Best-effort — revokes the current session server-side. Safe to call
  // even if the token is already gone/expired (backend returns {ok:true}
  // either way, per the router's own code).
  const res = await api.post("/auth/logout");
  return res.data;
}

export async function validate() {
  const res = await api.get("/auth/validate");
  return res.data; // { valid, user_id, role, user }
}

export async function updateProfile({ name, email, phone }) {
  const res = await api.patch("/auth/me", { name, email, phone });
  return res.data; // { access_token, user } — token is reissued, must be re-stored
}

// The exact same self-service endpoint frontend_Admin's MyProfilePage/
// PhotoPicker already call — not a new mechanism. `photo` is a base64
// data URL (or null to remove). No token is reissued here (email doesn't
// change), so unlike updateProfile above there's nothing to re-store
// beyond the photo value itself.
export async function updatePhoto(photo) {
  const res = await api.put("/auth/me/photo", { photo: photo || null });
  return res.data; // { photo }
}

export async function changePassword({ currentPassword, newPassword }) {
  const res = await api.post("/auth/change-password", {
    current_password: currentPassword,
    new_password: newPassword,
  });
  return res.data;
}

// Real, newly-added backend endpoint (routers/auth_router.py). Always
// resolves with the same generic { message } regardless of whether the
// email is a real account — the backend itself never reveals that, so
// there is nothing for this function to hide or normalize on top.
export async function forgotPassword(email) {
  const res = await api.post("/auth/forgot-password", { email });
  return res.data; // { message }
}

// `token` is the raw value from the emailed reset link's ?token= query
// param — sent as-is; the backend hashes it before ever comparing it
// against stored token_hash values. Never returns a token/session — the
// caller must log in normally afterward (no auto-login by design).
export async function resetPassword({ token, newPassword }) {
  const res = await api.post("/auth/reset-password", { token, new_password: newPassword });
  return res.data; // { message }
}
